import React, { createContext, useContext, useState } from "react";

// Demo starting cart so the /cart page has something to show right away.
// In a real app this would start empty and fill up as people add items.
const initialItems = [
  {
    id: 1,
    slug: "untamed-logo-hoodie",
    name: "Untamed Logo Hoodie",
    meta: "Size L · Black",
    price: 50000,
    img: "https://picsum.photos/seed/mu-hoodie-main/200/200",
    qty: 1
  },
  {
    id: 2,
    slug: "rema-calm-down-tour-tee",
    name: 'Rema "Calm Down" Tour Tee',
    meta: "Size M",
    price: 42000,
    img: "https://picsum.photos/seed/mu-tour-tee/200/200",
    qty: 2
  },
  {
    id: 3,
    slug: "culture-snapback-cap",
    name: "Culture Snapback Cap",
    meta: "One Size",
    price: 20000,
    img: "https://picsum.photos/seed/mu-merch-cap/200/200",
    qty: 1
  }
];

const SHIPPING = 1400;
const TAX = 500;

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(initialItems);

  function addItem(product, meta = "") {
    setItems((current) => {
      // If this exact product + option (size/color) is already in the cart,
      // just bump the quantity instead of adding a duplicate row.
      const existing = current.find((i) => i.slug === product.slug && i.meta === meta);
      if (existing) {
        return current.map((i) => (i.id === existing.id ? { ...i, qty: i.qty + 1 } : i));
      }
      const newItem = {
        id: Date.now(),
        slug: product.slug,
        name: product.name,
        meta,
        price: product.price,
        img: product.images ? product.images[0] : product.img,
        qty: 1
      };
      return [...current, newItem];
    });
  }

  function removeItem(id) {
    setItems((current) => current.filter((i) => i.id !== id));
  }

  function updateQty(id, qty) {
    setItems((current) =>
      current.map((i) => (i.id === id ? { ...i, qty: Math.max(1, qty) } : i))
    );
  }

  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const itemCount = items.reduce((sum, i) => sum + i.qty, 0);
  const shipping = items.length ? SHIPPING : 0;
  const tax = items.length ? TAX : 0;
  const total = subtotal + shipping + tax;

  const value = {
    items,
    addItem,
    removeItem,
    updateQty,
    subtotal,
    shipping,
    tax,
    total,
    itemCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// Small helper hook so pages/components can just call useCart() instead of
// importing useContext + CartContext everywhere.
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used inside a <CartProvider>");
  }
  return context;
}
